import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import logo from '../assets/images/logo.png';
import Button from '../components/Button';
import Input from '../components/Input';
import { isValidEmail, VALIDATION_MESSAGES } from '../utils/validation';

const ForgotPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!trimmed) {
      setError(VALIDATION_MESSAGES.EMAIL_REQUIRED);
      return;
    }
    if (!isValidEmail(trimmed)) {
      setError(VALIDATION_MESSAGES.EMAIL_INVALID);
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      await sendPasswordResetEmail(auth, trimmed);
      setSent(true);
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        setError(t('forgot_password.err_user_not_found'));
      } else if (err.code === 'auth/invalid-email') {
        setError(t('forgot_password.err_invalid_email'));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('forgot_password.err_too_many'));
      } else {
        setError(t('forgot_password.err_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          {/* Logo and Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-2">
              <img src={logo} alt="VisaConnect Logo" className="h-16 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('forgot_password.title')}</h1>
            <p className="text-sm text-gray-600">
              {sent
                ? t('forgot_password.sub_sent')
                : t('forgot_password.sub_prompt')}
            </p>
          </div>

          {sent ? (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-left">
                <p className="text-sm text-gray-700">
                  {t('forgot_password.sent_body', { email: email.trim() })}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {t('forgot_password.sent_hint')}
                </p>
              </div>
              <Button
                type="button"
                onClick={() => navigate('/sign-in')}
                className="w-full"
                style={{ backgroundColor: '#0085FF' }}
              >
                {t('forgot_password.back_to_sign_in')}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3 text-left">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Input
                label={t('forgot_password.email_placeholder')}
                type="email"
                name="email"
                placeholder={t('forgot_password.email_placeholder')}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                disabled={submitting}
                required
              />

              <Button
                type="submit"
                disabled={submitting}
                className="w-full mt-2"
                style={{ backgroundColor: '#0085FF' }}
              >
                {submitting ? t('forgot_password.sending') : t('forgot_password.send_reset')}
              </Button>
            </form>
          )}

          <div className="mt-6 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700 text-sm underline"
              onClick={() => navigate('/sign-in')}
            >
              {t('forgot_password.back_to_sign_in_arrow')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;
